import { __ } from "@wordpress/i18n";
import {
	useBlockProps,
	MediaUpload,
	MediaUploadCheck,
	InspectorControls,
} from "@wordpress/block-editor";
import {
	PanelBody,
	RangeControl,
	ToggleControl,
	SelectControl,
	Button,
} from "@wordpress/components";
import { Fragment, useLayoutEffect, useRef } from "@wordpress/element";
import { Swiper } from "swiper";
import { Autoplay, Navigation, Pagination } from "swiper/modules";

import "./style.scss";

export default function Edit({ attributes, setAttributes }) {
	const {
		images,
		carouselHeight,
		slidesPerView,
		speed,
		autoplay,
		showPagination,
		arrowStyle,
	} = attributes;

	const blockProps = useBlockProps();

	const onSelectImages = (newImages) => {
		setAttributes({
			images: newImages.map((img) => ({
				id: img.id,
				url: img.url,
				alt: img.alt,
			})),
		});
	};

	const removeImage = (index) => {
		const newImages = [...images];
		newImages.splice(index, 1);
		setAttributes({ images: newImages });
	};

	const swiperRef = useRef(null);

	useLayoutEffect(() => {
		// Don't do anything if the container isn't rendered or has no images
		if (!swiperRef.current || images.length === 0) {
			return;
		}

		// Build the Swiper configuration from attributes
		const swiperConfig = {
			loop: images.length > slidesPerView, // Loop only if there are enough images
			slidesPerView: slidesPerView,
			modules: [Autoplay, Navigation, Pagination],
			navigation: {
				nextEl: swiperRef.current.querySelector(".swiper-button-next"),
				prevEl: swiperRef.current.querySelector(".swiper-button-prev"),
			},
			speed: 1500,
		};

		if (autoplay) {
			swiperConfig.autoplay = {
				delay: speed,
				disableOnInteraction: false,
			};
		}

		if (showPagination) {
			swiperConfig.pagination = {
				el: swiperRef.current.querySelector(".swiper-pagination"),
				clickable: true,
			};
		}

		// Initialize Swiper
		const swiperInstance = new Swiper(swiperRef.current, swiperConfig);

		// This cleanup function is crucial. It runs before the effect runs again.
		return () => {
			swiperInstance.destroy();
		};
	}, [
		images,
		carouselHeight,
		slidesPerView,
		speed,
		autoplay,
		showPagination,
		arrowStyle,
	]);

	return (
		<Fragment>
			<InspectorControls>
				<PanelBody title={__("Carousel Settings", "dapoobembe")}>
					<RangeControl
						label={__("Carousel Height (px)", "dapoobembe")}
						value={carouselHeight}
						onChange={(val) => setAttributes({ carouselHeight: val })}
						min={150}
						max={1000}
						step={10}
					/>
					<RangeControl
						label={__("Slides per view", "dapoobembe")}
						value={slidesPerView}
						onChange={(val) => setAttributes({ slidesPerView: val })}
						min={1}
						max={6}
					/>
					<ToggleControl
						label={__("Autoplay", "dapoobembe")}
						checked={autoplay}
						onChange={(val) => setAttributes({ autoplay: val })}
					/>
					{autoplay && (
						<RangeControl
							label={__("Transition speed (ms)", "dapoobembe")}
							value={speed}
							onChange={(val) => setAttributes({ speed: val })}
							min={100}
							max={10000}
							step={100}
						/>
					)}

					<ToggleControl
						label={__("Show Pagination", "dapoobembe")}
						checked={showPagination}
						onChange={(val) => setAttributes({ showPagination: val })}
					/>

					<SelectControl
						label={__("Arrow Style", "dapoobembe")}
						value={arrowStyle}
						options={[
							{ label: "Default", value: "default" },
							{ label: "Minimal", value: "minimal" },
							{ label: "Bold", value: "bold" },
						]}
						onChange={(val) => setAttributes({ arrowStyle: val })}
					/>
				</PanelBody>
			</InspectorControls>

			<div {...blockProps}>
				<MediaUploadCheck>
					<MediaUpload
						onSelect={onSelectImages}
						allowedTypes={["image"]}
						multiple
						gallery
						value={images.map((img) => img.id)}
						render={({ open }) => (
							<Button variant="primary" onClick={open}>
								{images.length
									? __("Edit Images", "dapoobembe")
									: __("Select Images", "dapoobembe")}
							</Button>
						)}
					/>
				</MediaUploadCheck>

				{images.length > 0 && (
					<div
						className={`image-carousel swiper-container arrow-${arrowStyle}`}
						data-slides={slidesPerView}
						data-speed={speed}
						data-autoplay={autoplay ? "true" : "false"}
						data-pagination={showPagination ? "true" : "false"}
						ref={swiperRef}
						style={{ height: `${carouselHeight}px` }}
					>
						<div className="swiper-wrapper">
							{images.map((img, index) => (
								<div className="swiper-slide" key={index}>
									<img src={img.url} alt={img.alt} loading="lazy" />
								</div>
							))}
						</div>
						<div className="swiper-button-prev"></div>
						<div className="swiper-button-next"></div>
						{showPagination && <div className="swiper-pagination"></div>}
					</div>
				)}
			</div>
		</Fragment>
	);
}
